import type {
  AgentCapabilities,
  AgentCapabilityMode,
  AgentEditingCapability,
  AgentPublishingCapability,
  AgentCapabilityUnavailableReason,
  AgentMode,
} from '@tabbio/types';
import { api } from './api';

const UNAVAILABLE_REASONS: readonly AgentCapabilityUnavailableReason[] = [
  'disabled',
  'not_allowlisted',
  'not_configured',
  'upgrade_required',
];

function readReason(value: unknown): AgentCapabilityUnavailableReason | undefined {
  return typeof value === 'string' &&
    (UNAVAILABLE_REASONS as readonly string[]).includes(value)
    ? (value as AgentCapabilityUnavailableReason)
    : undefined;
}

function readDefault(value: unknown): AgentCapabilityMode {
  return value === 'on' || value === 'off' ? value : 'auto';
}

function readToggle(value: unknown): AgentCapabilities['research'] {
  const record =
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const available = record.available === true;
  const reason = available ? undefined : readReason(record.reason) ?? 'disabled';
  return {
    available,
    default: readDefault(record.default),
    ...(reason ? { reason } : {}),
  };
}

function readCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

/**
 * Publishing (Phase 2, plan 5.3): kept only when the API sends the block; an
 * API that predates it leaves it out, which reads as unavailable.
 */
function readPublishing(value: unknown): AgentPublishingCapability | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const available = record.available === true;
  const reason = available ? undefined : readReason(record.reason) ?? 'disabled';
  const liveSites =
    record.liveSites && typeof record.liveSites === 'object'
      ? (record.liveSites as Record<string, unknown>)
      : {};
  return {
    available,
    ...(reason ? { reason } : {}),
    baseDomain:
      typeof record.baseDomain === 'string' && record.baseDomain ? record.baseDomain : 'tabbio.site',
    liveSites: { used: readCount(liveSites.used), limit: readCount(liveSites.limit) },
    canHideBadge: record.canHideBadge === true,
  };
}

/**
 * Shape the envelope's data defensively: a missing or malformed field reads as
 * unavailable, so a partial answer hides a control rather than offering one the
 * server would refuse.
 */
/** The editing block (plan 5.3): versions, manual edits and agent edits. */
function readEditing(value: unknown): AgentEditingCapability | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const source = value as Record<string, unknown>;
  const available = source.available === true;
  const reason = available ? undefined : readReason(source.reason);
  return { available, ...(reason ? { reason } : {}) };
}

export function normalizeAgentCapabilities(value: unknown): AgentCapabilities {
  const record =
    value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const artifacts =
    record.artifacts && typeof record.artifacts === 'object'
      ? (record.artifacts as Record<string, unknown>)
      : {};
  const artifactsAvailable = artifacts.available === true;
  const artifactsReason = artifactsAvailable
    ? undefined
    : readReason(artifacts.reason) ?? 'disabled';
  const publishing = readPublishing(record.publishing);
  const editing = readEditing(record.editing);
  const writer =
    record.writer && typeof record.writer === 'object'
      ? { available: (record.writer as Record<string, unknown>).available === true }
      : undefined;
  return {
    research: readToggle(record.research),
    image: readToggle(record.image),
    artifacts: {
      available: artifactsAvailable,
      ...(artifactsReason ? { reason: artifactsReason } : {}),
    },
    ...(editing ? { editing } : {}),
    ...(writer ? { writer } : {}),
    ...(publishing ? { publishing } : {}),
  };
}

/** GET /api/agent/capabilities: what the composer controls may offer this user. */
export async function fetchAgentCapabilities(
  mode: AgentMode = 'seeker',
): Promise<AgentCapabilities> {
  const res = await api.get<AgentCapabilities>('/api/agent/capabilities', {
    headers: { 'x-agent-mode': mode },
  });
  if (res.error || !res.data) {
    throw new Error(res.error?.message ?? 'Agent capabilities are unavailable.');
  }
  return normalizeAgentCapabilities(res.data);
}
